import * as QuillLib from "quill";
import React, { useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";

import QuillImageDropAndPaste, { ImageData } from "quill-image-drop-and-paste";
import ImageResize from "quill-image-resize-module-react";

import { InserirArquivoDriveController } from "../../../../Controllers/Geral/Integracao/IntegracoesController";
import { getBase64URL } from "../../../../Tools/ProcessingTools";
import { useTransformToRef } from "../../../../hooks/useTransformToRef";

//#region interface do React Quill que não consegui importar
interface ComponentProps {
    id?: string;
    className?: string;
    theme?: string;
    style?: React.CSSProperties;
    readOnly?: boolean;
    value?: string | QuillLib.Delta;
    defaultValue?: string | QuillLib.Delta;
    placeholder?: string;
    tabIndex?: number;
    bounds?: string | HTMLElement;
    scrollingContainer?: string | HTMLElement;
    onChange?: (
        content: string,
        delta: QuillLib.Delta,
        source: QuillLib.Sources,
        editor: UnprivilegedEditor
    ) => void;
    onChangeSelection?: (
        range: QuillLib.RangeStatic,
        source: QuillLib.Sources,
        editor: UnprivilegedEditor
    ) => void;
    onFocus?: (
        range: QuillLib.RangeStatic,
        source: QuillLib.Sources,
        editor: UnprivilegedEditor
    ) => void;
    onBlur?: (
        previousRange: QuillLib.RangeStatic,
        source: QuillLib.Sources,
        editor: UnprivilegedEditor
    ) => void;
    onKeyPress?: React.EventHandler<any>;
    onKeyDown?: React.EventHandler<any>;
    onKeyUp?: React.EventHandler<any>;
    formats?: string[];
    children?: React.ReactElement<any>;
    modules?: QuillLib.StringMap;
    preserveWhitespace?: boolean;

    /** @deprecated
     * The `toolbar` prop has been deprecated. Use `modules.toolbar` instead.
     * See: https://github.com/zenoamaro/react-quill#upgrading-to-react-quill-v100.
     * */

    toolbar?: never;
    /** @deprecated
     * The `styles` prop has been deprecated. Use custom stylesheets instead.
     * See: https://github.com/zenoamaro/react-quill#upgrading-to-react-quill-v100
     */

    styles?: never;
    /**
     * @deprecated
     * The `pollInterval` property does not have any effect anymore.
     * You can safely remove it from your props.
     * See: https://github.com/zenoamaro/react-quill#upgrading-to-react-quill-v100.
     */
    pollInterval?: never;
}
interface UnprivilegedEditor {
    getLength(): number;
    getText(index?: number, length?: number): string;
    getHTML(): string;
    getBounds(index: number, length?: number): QuillLib.BoundsStatic;
    getSelection(focus?: boolean): QuillLib.RangeStatic;
    getContents(index?: number, length?: number): QuillLib.DeltaStatic;
}
//#endregion

interface IEditorProps extends ComponentProps {
    idCaso?: number;
    onImageUploadStart?: () => void;
    onImageUploadEnd?: () => void;
}

Quill.register("modules/imageDropAndPaste", QuillImageDropAndPaste);
Quill.register("modules/imageResize", ImageResize);

export default function Editor(props: IEditorProps) {
    const { idCaso, onImageUploadStart, onImageUploadEnd, ...quillProps } =
        props;

    // salvando função em ref para parar com warning de exhautive-deps
    // sem causar execuções desnecessárias do useMemo
    const onImageUploadStartRef = useTransformToRef(onImageUploadStart);
    const onImageUploadEndRef = useTransformToRef(onImageUploadEnd);

    const quill = useRef<ReactQuill>(null);

    const modules = useMemo(() => {
        return {
            toolbar: {
                container: [
                    [
                        { header: "1" },
                        { header: "2" },
                        {
                            font: [
                                "sofia",
                                "slabo",
                                "roboto",
                                "inconsolata",
                                "ubuntu",
                            ],
                        },
                    ],
                    [
                        {
                            color: [
                                "#000000",
                                "#e60000",
                                "#ff9900",
                                "#ffff00",
                                "#008a00",
                                "#0066cc",
                                "#9933ff",
                                "#ffffff",
                                "#facccc",
                                "#ffebcc",
                                "#ffffcc",
                                "#cce8cc",
                                "#cce0f5",
                                "#ebd6ff",
                                "#bbbbbb",
                                "#f06666",
                                "#ffc266",
                                "#ffff66",
                                "#66b966",
                                "#66a3e0",
                                "#c285ff",
                                "#888888",
                                "#a10000",
                                "#b26b00",
                                "#b2b200",
                                "#006100",
                                "#0047b2",
                                "#6b24b2",
                                "#444444",
                                "#5c0000",
                                "#663d00",
                                "#666600",
                                "#003700",
                                "#002966",
                                "#3d1466",
                                "custom-color",
                            ],
                        },
                    ],
                    [
                        {
                            background: [
                                "#000000",
                                "#e60000",
                                "#ff9900",
                                "#ffff00",
                                "#008a00",
                                "#0066cc",
                                "#9933ff",
                                "#ffffff",
                                "#facccc",
                                "#ffebcc",
                                "#ffffcc",
                                "#cce8cc",
                                "#cce0f5",
                                "#ebd6ff",
                                "#bbbbbb",
                                "#f06666",
                                "#ffc266",
                                "#ffff66",
                                "#66b966",
                                "#66a3e0",
                                "#c285ff",
                                "#888888",
                                "#a10000",
                                "#b26b00",
                                "#b2b200",
                                "#006100",
                                "#0047b2",
                                "#6b24b2",
                                "#444444",
                                "#5c0000",
                                "#663d00",
                                "#666600",
                                "#003700",
                                "#002966",
                                "#3d1466",
                                "custom-color",
                            ],
                        },
                    ],
                    [{ size: [] }],
                    ["bold", "italic", "underline", "strike", "blockquote"],
                    [
                        { list: "ordered" },
                        { list: "bullet" },
                        { indent: "-1" },
                        { indent: "+1" },
                    ],
                    ["link", "image", "video"],
                    ["clean"],
                ],
                handlers: {
                    // substituindo o comportamento do botão de imagem para adicionar carregamento de imagens no drive
                    image: () => {
                        //pegando o arquivo manualmente
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = "image/*";
                        input.click();

                        input.onchange = async () => {
                            const file = input.files?.item(0);
                            if (file) {
                                const image = {
                                    url: "",
                                };
                                if (idCaso) {
                                    onImageUploadStartRef.current?.();
                                    image.url =
                                        await InserirArquivoDriveController(
                                            idCaso,
                                            file
                                        ).finally(onImageUploadEndRef.current);
                                } else {
                                    image.url = await getBase64URL(file);
                                }
                                quill.current
                                    ?.getEditor()
                                    .insertEmbed(
                                        quill.current.getEditor().getSelection()
                                            ?.index ?? 0,
                                        "image",
                                        image.url
                                    );
                            }
                        };
                    },
                },
            },
            clipboard: {
                //toggle to add extra line breaks when pasting HTML:
                matchVisual: false,
            },
            imageResize: {
                parchment: Quill.import("parchment"),
            },
            imageDropAndPaste: {
                // add an custom image handler
                handler: async (
                    imageDataUrl: string,
                    type: string,
                    imageData: ImageData
                ) => {
                    const image = {
                        url: imageDataUrl,
                    };
                    if (idCaso) {
                        // se é passado idCaso a imagem será salva no drive
                        const file = imageData.toFile();
                        if (file) {
                            onImageUploadStartRef.current?.();
                            image.url = await InserirArquivoDriveController(
                                idCaso,
                                file
                            ).finally(onImageUploadEndRef.current);
                        }
                    }
                    // insere imagem no quill, a url
                    quill.current
                        ?.getEditor()
                        .insertEmbed(
                            quill.current.getEditor().getSelection()?.index ??
                                0,
                            "image",
                            image.url
                        );
                },
            },
        };
    }, [idCaso, onImageUploadStartRef, onImageUploadEndRef]);

    const formats = [
        "header",
        "font",
        "size",
        "bold",
        "italic",
        "underline",
        "strike",
        "blockquote",
        "list",
        "bullet",
        "indent",
        "link",
        "image",
        "video",
        "color",
        "background",
    ];

    return (
        <ReactQuill
            theme="snow"
            className="custom-quill mb-5"
            modules={modules}
            formats={formats}
            ref={quill}
            bounds={".app"}
            {...quillProps}
        />
    );
}
